import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';

import compose from 'helpers/compose';
import Switch from 'components/FormFields/Switch';
import ControlButtons from 'components/ControlButtons';
import FormTextField from 'components/FormFields/FormTextField';
import Background from 'components/Layout/Background';
import DatePicker from 'components/FormFields/FormDatePicker';
import Info from 'components/Tooltip/Info';

import { EditAccountStore } from 'stores/editAccountStore';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { RootStore } from 'stores/rootStore';
import { WorkflowAdditionalInfoStore } from 'stores/workflowStores/workflowAdditionalInfoStore/workflowAdditionalInfoStore';
import { AdditionalInfoData, initialValues } from 'stores/workflowStores/workflowAdditionalInfoStore/model';
import { ContractTab } from '../Contract';
import { ClientView, AccountMode } from 'enums';
import { Permission } from 'helpers/permissions';
import FormSelectField from 'components/FormFields/FormSelectField';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > div > div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiFormControl-root': {
        marginRight: theme.spacing(1),
      },
    },
  }),
);

interface Props {
  rootStore: RootStore;
  editAccountStore?: EditAccountStore;
  tabChange: (tab: ContractTab) => void;
}

const AdditionalInfo: React.FC<Props> = ({ rootStore, editAccountStore, tabChange }) => {
  const { accountId } = useParams<Params>();
  const [store] = useState(new WorkflowAdditionalInfoStore(rootStore, accountId));
  const { handleNext, handleBack, data } = store;
  const { t } = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    store.fetchAdditionalInfoData();
  }, [store]);

  const handleSubmit = (values: AdditionalInfoData, formikHelpers: FormikHelpers<AdditionalInfoData>) => {
    handleNext(tabChange, values, formikHelpers);
  };

  return (
    data && (
      <Formik initialValues={merge({}, initialValues, data)} onSubmit={handleSubmit}>
        {(formikProps: FormikProps<AdditionalInfoData>) => (
          <Form>
            <Background afterMenu>
              <Background inside>
                <Grid container>
                  <Grid item xs={6}>
                    <FormTextField
                      name="title"
                      label={t('general_accountName')}
                      afterChange={() => rootStore.stores.uiStore.updateNoteTitle(formikProps.values.title)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormTextField name="signatureLocation" label={t('contract_data_signatureLocation')} />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker name={`verificationDate`} label={t('contract_data_verificationDate')} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h3">{t('contract_data_meetingRecordNote_title')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      name="meetingRecordNote"
                      //label={t('contract_aml_q_fraudSuspicion_reasonDetail')}
                      placeholder={t('contract_data_meetingRecordNote_placeholder')}
                      multiline={true}
                      rows={6}
                      maxLength={2000}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  {rootStore.stores.userStore.checkPermissions(Permission.ACCOUNT_SET_CLIENTVIEW) && (
                    <>
                      <Grid item xs={12} />
                      <Grid item xs={12}>
                        <Typography variant="h3">{t('client_access')}</Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <FormControl component="fieldset" className={classes.root}>
                          <RadioGroup
                            name="clientView"
                            value={formikProps.values.clientView}
                            onChange={(e) => formikProps.setFieldValue('clientView', e.target.value as ClientView)}
                          >
                            <div>
                              <FormControlLabel
                                value={ClientView.BASIC}
                                control={<Radio />}
                                label={t(`client_access_${ClientView.BASIC}`)}
                              />
                              <Info content={t(`client_access_${ClientView.BASIC}_tooltip`)} inline />
                            </div>
                            <div>
                              <FormControlLabel
                                value={ClientView.FULL}
                                control={<Radio />}
                                label={t(`client_access_${ClientView.FULL}`)}
                                disabled={formikProps.values.use2fa === false}
                              />
                              <Info
                                content={
                                  <>
                                    {t('client_access_FULL_tooltip')}
                                    <br />
                                    {formikProps.values.use2fa === false && (
                                      <strong>{t('client_access_FULL2FA_tooltip')}</strong>
                                    )}
                                  </>
                                }
                                inline
                              />
                            </div>
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} />
                  <Grid item xs={6}>
                    <Switch
                      name={`use2fa`}
                      label={`${t('account_2fa')}`}
                      tooltipContent={
                        <>
                          {t('tooltip_account_2fa')}
                          {formikProps.values.clientView === ClientView.FULL && (
                            <>
                              <br />
                              <strong>{t('tooltip_account_2fa_basicView')}</strong>
                            </>
                          )}
                        </>
                      }
                      disabled={formikProps.values.clientView === ClientView.FULL}
                    />
                  </Grid>
                  {formikProps.values.use2fa && (
                    <>
                      <Grid item xs={6} />
                      <Grid item xs={6}>
                        <FormSelectField
                          name={`method`}
                          label={t('account_2fa_method')}
                          options={data.options.method.map((method) => ({
                            value: method,
                            label: t(`account_2fa_method_${method}`),
                          }))}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} />
                </Grid>
                <Grid container>
                  <Grid item xs={12} />
                  <Grid item xs={12}>
                    <Typography variant="h3">{t('client_emailNotifications')}</Typography>
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <Grid item xs={12}>
                      <Switch name={`notification.notificationMonthlyStatement`} label={`${t('pdf_document_MONTH')}`} />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch name={`notification.notificationYearlyStatement`} label={`${t('pdf_document_YEAR')}`} />
                    </Grid>
                    {editAccountStore?.mode === AccountMode.LTIP && (
                      <Grid item xs={12}>
                        <Switch name={`notification.ltipSummary`} label={`${t('pdf_document_LTIP_SUMMARY')}`} />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Switch name={`notification.ltipSummary`} label={`${t('pdf_document_LTIP_SUMMARY')}`} />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch name={`notification.newPayment`} label={`${t('client_emailNotification_newPayment')}`} />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch
                        name={`notification.paymentSent`}
                        label={`${t('client_emailNotification_paymentSent')}`}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch
                        name={`notification.targetClose`}
                        label={`${t('client_emailNotification_targetClose')}`}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch
                        name={`notification.targetWithdrawal`}
                        label={`${t('client_emailNotification_targetWithdrawal')}`}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch name={`notification.depositDue`} label={`${t('client_emailNotification_depositDue')}`} />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch
                        name={`notification.depositClose`}
                        label={`${t('client_emailNotification_depositClose')}`}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h3">{t('client_reports')}</Typography>
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <Grid item xs={12}>
                      <Switch
                        name={`notification.showFullOverview`}
                        label={`${t('client_showFullOverview')}`}
                        tooltipContent={t('client_showFullOverview_tooltip')}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Background>
            </Background>

            <ControlButtons
              isSubmitting={formikProps.isSubmitting}
              nextText={t('contract_docs')}
              onBack={() => handleBack(tabChange)}
              backText={t('contract_aml')}
            />
          </Form>
        )}
      </Formik>
    )
  );
};

export default compose(inject('rootStore', 'editAccountStore'), observer)(AdditionalInfo);
